/** RTK Query helpers for withdrawal/deposit list parsing and cache tags. */

import { WITHDRAWAL_DEPOSIT_FORM_LIST_TAG } from "helpers/withdrawalDepositConstants";
import type {
  WithdrawalDepositForm,
  WithdrawalDepositFormsListResponse,
  WithdrawalDepositFormsPagination,
  WithdrawalDepositFormsQueryArg,
} from "types/withdrawalDeposit";

export type WithdrawalDepositCacheTag =
  | "EWALLET_TRANSACTIONS"
  | { type: "WITHDRAWAL_DEPOSIT_FORM"; id: string }
  | { type: "WITHDRAWAL_DEPOSIT_NOTES"; id: string };

export function extractWithdrawalDepositForms(
  response: unknown,
): WithdrawalDepositForm[] {
  if (Array.isArray(response)) {
    return response as WithdrawalDepositForm[];
  }
  if (!response || typeof response !== "object") {
    return [];
  }
  const record = response as Record<string, unknown>;
  const data = record.data;
  if (Array.isArray(data)) {
    return data as WithdrawalDepositForm[];
  }
  if (data && typeof data === "object") {
    const nested = data as Record<string, unknown>;
    for (const key of [
      "forms",
      "withdrawal_deposit_forms",
      "withdrawal_deposit_requests",
      "rows",
      "items",
      "results",
    ]) {
      if (Array.isArray(nested[key])) {
        return nested[key] as WithdrawalDepositForm[];
      }
    }
  }
  for (const key of ["forms", "rows", "items", "results"]) {
    if (Array.isArray(record[key])) {
      return record[key] as WithdrawalDepositForm[];
    }
  }
  return [];
}

export function extractWithdrawalDepositFormsPagination(
  response: unknown,
): WithdrawalDepositFormsPagination | undefined {
  if (!response || typeof response !== "object") {
    return undefined;
  }
  const record = response as Record<string, unknown>;
  const candidates = [
    record.pagination,
    (record.data as Record<string, unknown> | undefined)?.pagination,
  ];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") {
      // skip non-object pagination payloads
    } else {
      const pagination = candidate as Record<string, unknown>;
      const current_page = Number(pagination.current_page);
      const last_page = Number(pagination.last_page);
      const total = Number(pagination.total);
      const per_page = Number(pagination.per_page);
      if (
        Number.isFinite(current_page) &&
        Number.isFinite(last_page) &&
        Number.isFinite(total) &&
        Number.isFinite(per_page)
      ) {
        return { current_page, last_page, total, per_page };
      }
    }
  }
  return undefined;
}

/** Build query string for GET /withdrawl-deposit/forms (shared by RTK and callers). */
export function buildWithdrawalDepositFormsQueryString(
  params?: WithdrawalDepositFormsQueryArg,
): string {
  if (!params) {
    return "";
  }
  const queryParams = new URLSearchParams();
  if (params.status) {
    queryParams.append("status", params.status);
  }
  if (params.page != null) {
    queryParams.append("page", String(params.page));
  }
  if (params.per_page != null) {
    queryParams.append("per_page", String(params.per_page));
  }
  if (params.search) {
    queryParams.append("search", params.search);
  }
  const qs = queryParams.toString();
  return qs ? `?${qs}` : "";
}

export function extractWithdrawalDepositFormsList(
  response: unknown,
): WithdrawalDepositFormsListResponse {
  return {
    forms: extractWithdrawalDepositForms(response),
    pagination: extractWithdrawalDepositFormsPagination(response),
  };
}

export function withdrawalDepositFormInvalidateTags(
  formId: string,
): WithdrawalDepositCacheTag[] {
  return [
    "EWALLET_TRANSACTIONS",
    { type: "WITHDRAWAL_DEPOSIT_FORM", id: WITHDRAWAL_DEPOSIT_FORM_LIST_TAG },
    { type: "WITHDRAWAL_DEPOSIT_FORM", id: formId },
  ];
}

export function withdrawalDepositNotesInvalidateTags(
  formId: string,
): WithdrawalDepositCacheTag[] {
  return [
    ...withdrawalDepositFormInvalidateTags(formId),
    { type: "WITHDRAWAL_DEPOSIT_NOTES", id: formId },
  ];
}
